import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ASSET_KEYS, assetLineCalc, Exact, type AssetInvoiceDto, type AssetInvoiceInput, type AssetInvoiceListDto, type AssetInvoicesDto, type InvoiceReverseInput } from "@delta/shared";
import { useLang } from "../../i18n";
import { useSession } from "../../lib/session";
import { api, ApiFailure } from "../../lib/api";
import { errorText, fieldErrorsOf } from "../../lib/errors";
import { Icon } from "../../components/Icon";
import { PageHead, Shell } from "../../components/Shell";
import { ListView, type Column } from "../../components/ListView";
import { Field, Modal } from "../../components/Modal";
import { useToast } from "../../components/Toast";
import { LockedButton } from "../settings/common";
import { Card } from "../accounting/common";
import { amt, nowRiyadh, when } from "../ledger/common";
import { AssetProvisional, assetCrumbs, useAssetOptions } from "./Assets";

// Fixed assets › Asset purchases: the list, the new purchase and the purchase page, ported from the prototype
// (erp-assets.js "/assets/purchases", "/assets/new-purchase", "/assets-purchase/show/:id"). Rule T-3, provisional: a
// purchase posts at once (no drafts), each line becomes one asset at its value before VAT, and a purchase is corrected
// by reversing it while none of its assets has deported depreciation. The server works out and posts every figure;
// the totals shown while typing use the same shared calculation.

const INV = ASSET_KEYS.invoices;
const nowLocal = nowRiyadh;
const show = (id: string) => `/assets-purchase/show/${id}`;

function Status({ x }: { x: AssetInvoiceListDto }) {
  const { t } = useLang();
  return x.reversedAt ? <span className="pill" data-testid="ap-status">{t("journalReversed")}</span> : <span className="pill good" data-testid="ap-status">{t("journalPosted")}</span>;
}

export function AssetPurchases() {
  const { t, L } = useLang();
  const s = useSession();
  const nav = useNavigate();
  const o = useAssetOptions();
  const [data, setData] = useState<AssetInvoicesDto | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const load = useCallback(async () => {
    setFailed(null);
    const q = [filters.no && `q=${encodeURIComponent(filters.no)}`, filters.wh && `warehouseId=${filters.wh}`, filters.sup && `partyId=${filters.sup}`].filter(Boolean).join("&");
    try { setData(await api<AssetInvoicesDto>("GET", `/asset-invoices${q ? "?" + q : ""}`)); } catch (e) { setFailed(errorText(t, e)); }
  }, [filters.no, filters.wh, filters.sup, t]);
  useEffect(() => { void load(); }, [load]);
  const columns: Column<AssetInvoiceListDto>[] = [
    { k: "no", label: t("invoiceNo"), cell: (x) => <Link className="num" to={show(x.id)}>{x.code}</Link>, csv: (x) => x.code },
    { k: "acc", label: t("account"), cell: (x) => <span className="cell-name">{x.party.name}</span>, csv: (x) => x.party.name },
    { k: "wh", label: t("warehouse"), cell: (x) => L(x.warehouse), csv: (x) => L(x.warehouse) },
    { k: "by", label: t("by"), cell: (x) => x.createdBy || "–", csv: (x) => x.createdBy },
    { k: "grand", label: t("grandTotal"), end: true, cell: (x) => <span className="num">{amt(x.grand)}</span>, csv: (x) => x.grand },
    { k: "paid", label: t("paidTotal"), end: true, cell: (x) => <span className="num">{amt(x.paid)}</span>, csv: (x) => x.paid },
    { k: "rem", label: t("remaining"), end: true, cell: (x) => <span className="num">{amt(x.remaining)}</span>, csv: (x) => x.remaining },
    { k: "status", label: t("status"), cell: (x) => <Status x={x} />, csv: (x) => (x.reversedAt ? "reversed" : "posted") },
    { k: "date", label: t("date"), cell: (x) => <span className="num">{when(x.invoiceAt)}</span>, csv: (x) => when(x.invoiceAt) }
  ];
  const newBtn = s.can(INV.add) ? <Link className="btn btn-primary" to="/assets/new-purchase"><Icon name="plus" />{t("newInvoice")}</Link>
    : <LockedButton label={t("newInvoice")} title={t("noPermissionFor", { p: INV.add })} />;
  const above = data && <div className="ltb-above"><span className="hint" data-testid="ap-totals">{t("grandTotal")}: <b className="num">{amt(data.total.grand)}</b> · {t("paidTotal")}: <b className="num">{amt(data.total.paid)}</b> · {t("remaining")}: <b className="num">{amt(data.total.remaining)}</b></span></div>;
  return (
    <Shell navKey="/assets/purchases">
      <PageHead crumbs={assetCrumbs(t, t("assetPurchases"))} title={t("assetPurchases")} />
      <AssetProvisional />
      <ListView rows={data?.invoices ?? null} error={failed} columns={columns} file="asset-purchases" testId="ap-table" onRefresh={() => void load()} above={above}
        search={[{ k: "no", label: t("invoiceNo"), type: "text" }, { k: "wh", label: t("warehouse"), type: "select", options: (o?.warehouses ?? []).map((w) => [w.id, L(w)]) },
          { k: "sup", label: t("supplier"), type: "select", options: (o?.suppliers ?? []).map((x) => [x.id, x.name]) }]} filters={filters} onFilters={setFilters}
        quick={(x) => `${x.code} ${x.party.name}`} newButton={newBtn}
        empty={{ icon: "building", title: t("noResults"), body: t("noResultsBody") }}
        actions={(x) => [{ label: t("view"), icon: "eye", run: () => nav(show(x.id)) }, { label: t("print"), icon: "printer", run: () => nav(show(x.id)) }]} />
    </Shell>
  );
}

// ---------- new purchase ----------

interface Line { key: number; statement: string; kindId: string; qty: string; price: string; taxTypeId: string; sel?: boolean }
let seq = 0;
const NUM_RE = /^\d{1,13}(\.\d{1,2})?$/;
const QTY_RE = /^\d{1,9}$/;
const ok = (x: string, re: RegExp) => re.test(x.trim()) && /[1-9]/.test(x);

export function NewAssetPurchase() {
  const { t, L } = useLang();
  const nav = useNavigate();
  const toast = useToast();
  const o = useAssetOptions();
  const blank = (): Line => ({ key: ++seq, statement: "", kindId: "", qty: "1", price: "", taxTypeId: o?.taxTypes[0]?.id ?? "" });
  const [at, setAt] = useState(nowLocal);
  const [wh, setWh] = useState("");
  const [party, setParty] = useState("");
  const [treasury, setTreasury] = useState("");
  const [paid, setPaid] = useState("");
  const [ref, setRef] = useState("");
  const [note, setNote] = useState("");
  const [lines, setLines] = useState<Line[]>(() => [blank()]);
  const [bad, setBad] = useState<Set<number>>(new Set());
  const [alert, setAlert] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!o) return;
    setWh((w) => w || o.warehouses[0]?.id || "");
    setLines((ls) => ls.map((l) => (l.taxTypeId ? l : { ...l, taxTypeId: o.taxTypes[0]?.id ?? "" })));
  }, [o]);
  const rate = (id: string) => o?.taxTypes.find((x) => x.id === id)?.rate ?? "0";
  const calc = (l: Line) => (ok(l.qty, QTY_RE) && ok(l.price, NUM_RE) && l.taxTypeId ? assetLineCalc(l.qty.trim(), l.price.trim(), rate(l.taxTypeId)) : null);
  const used = lines.filter((l) => l.statement.trim() || l.price.trim());
  const rows = used.map(calc);
  const sum = (k: "net" | "tax" | "total") => Exact.fmt(rows.reduce((a, r) => (r ? Exact.add(a, Exact.dec(r[k])) : a), Exact.dec("0")), 2);
  const sub = sum("net"), vat = sum("tax"), grand = sum("total");
  const paidV = NUM_RE.test(paid.trim()) ? paid.trim() : "0";
  const remaining = Exact.fmt(Exact.sub(Exact.dec(grand), Exact.dec(paidV)), 2);
  const set = (key: number, x: Partial<Line>) => { setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...x } : l))); setBad((b) => { const n = new Set(b); n.delete(key); return n; }); setAlert(null); };
  async function save() {
    if (!party) { setAlert(`${t("requiredField")}: ${t("supplier")}`); return; }
    if (!used.length) { setAlert(t("assetLineInvalid")); return; }
    const missing = used.filter((l) => !l.kindId || !l.taxTypeId);
    if (missing.length) { setBad(new Set(missing.map((l) => l.key))); setAlert(t("chooseKindTax")); return; }
    const wrong = used.filter((l) => !l.statement.trim() || !calc(l));
    if (wrong.length) { setBad(new Set(wrong.map((l) => l.key))); setAlert(t("assetLineInvalid")); return; }
    if (paid.trim() && !NUM_RE.test(paid.trim())) { setAlert(t("twoDecimals")); return; }
    if (remaining.startsWith("-")) { setAlert(t("paidOverGrand")); return; }
    if (/[1-9]/.test(paidV) && !treasury) { setAlert(`${t("requiredField")}: ${t("treasury")}`); return; }
    const body: AssetInvoiceInput = {
      invoiceAt: at, warehouseId: wh, partyId: party, treasuryId: treasury || null, paid: paidV, reference: ref.trim(), note: note.trim(),
      lines: used.map((l) => ({ statement: l.statement.trim(), kindId: l.kindId, qty: l.qty.trim(), price: l.price.trim(), taxTypeId: l.taxTypeId }))
    };
    setBusy(true); setAlert(null);
    try {
      const x = await api<AssetInvoiceDto>("POST", "/asset-invoices", body);
      toast(t("assetsCreated", { no: x.code, n: x.assets.length }));
      nav(show(x.id));
    } catch (err) {
      const f = fieldErrorsOf(t, err);
      const lineErr = Object.keys(f).map((k) => /^lines\.(\d+)\./.exec(k)?.[1]).filter((n): n is string => n != null).map((n) => used[Number(n)]?.key).filter((k): k is number => k != null);
      setBad(new Set(lineErr));
      setAlert(Object.values(f)[0] ?? errorText(t, err));
    } finally { setBusy(false); }
  }
  const anySel = lines.some((l) => l.sel);
  const sel = (id: string, label: string, value: string, onChange: (v: string) => void, list: [string, string][], req?: boolean, testId?: string) => (
    <div className="field"><label className={req ? "req" : ""} htmlFor={id}>{label}</label>
      <select id={id} className="control" value={value} onChange={(e) => { onChange(e.target.value); setAlert(null); }} disabled={!o} data-testid={testId}><option value="">{t("choose")}</option>{list.map(([a, b]) => <option key={a} value={a}>{b}</option>)}</select></div>
  );
  const bar = (
    <div className="action-bar"><div className="ab-stats">
      <div><div className="k">{t("subTotal")}</div><div className="v num" data-testid="ap-sub">{amt(sub)}</div></div>
      <div className="ab-hide"><div className="k">{t("tax")}</div><div className="v num" data-testid="ap-vat">{amt(vat)}</div></div>
      <div className="ab-grand"><div className="k">{t("grandTotal")}</div><div className="v"><span className="num" data-testid="ap-grand">{amt(grand)}</span> <span className="cur">{t("currency")}</span></div></div>
      <div className="ab-hide"><div className="k">{t("paid")}</div><div className="v num">{amt(paidV)}</div></div>
      <div><div className="k">{t("remaining")}</div><div className="v num" data-testid="ap-rem">{amt(remaining)}</div></div>
    </div><div className="ab-actions">
      <Link className="btn" to="/assets/purchases">{t("cancel")}</Link>
      <button className="btn btn-primary" onClick={() => void save()} disabled={busy || !o} data-testid="ap-save"><Icon name="check" />{busy ? t("saving") : t("save")}</button>
    </div></div>
  );
  return (
    <Shell navKey="/assets/purchases" bar={bar}>
      <PageHead crumbs={assetCrumbs(t, t("assetPurchases"), "/assets/purchases", [[t("createAssetsPurchase")]])} title={t("createAssetsPurchase")} />
      <AssetProvisional />
      <div className="dep-note" data-testid="ap-note"><Icon name="info" /><span>{t("assetPurchaseNote")}</span></div>
      <div className="inv-stack">
        <Card title={t("invoiceData")}><div className="form-grid">
          <div className="field"><label className="req" htmlFor="ap-dt">{t("invoiceDateTime")}</label>
            <input id="ap-dt" className="control num" style={{ textAlign: "start" }} type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} /></div>
          {sel("ap-supplier", t("supplier"), party, setParty, (o?.suppliers ?? []).map((x) => [x.id, x.name]), true, "ap-supplier")}
          {sel("ap-warehouse", t("warehouse"), wh, setWh, (o?.warehouses ?? []).map((x) => [x.id, L(x)]), true)}
          {sel("ap-treasury", t("treasury"), treasury, setTreasury, (o?.treasuries ?? []).filter((x) => x.accountId).map((x) => [x.id, L(x)]), false, "ap-treasury")}
          <div className="field"><label htmlFor="ap-paid">{t("paidAmount")}</label>
            <input id="ap-paid" className="control num" style={{ textAlign: "start" }} dir="ltr" inputMode="decimal" value={paid} onChange={(e) => { setPaid(e.target.value); setAlert(null); }} data-testid="ap-paid" /></div>
          <div className="field"><label htmlFor="ap-ref">{t("paidReference")}</label><input id="ap-ref" className="control" value={ref} maxLength={100} onChange={(e) => setRef(e.target.value)} /></div>
          <div className="field span-2"><label htmlFor="ap-note">{t("note")}</label><input id="ap-note" className="control" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} /></div>
        </div></Card>
        <Card title={t("invoiceItems")} count={lines.length} raw>
          <div className="lines-toolbar"><span className="hint">{t("chooseKindTax")}</span><span className="spacer" />
            <button className="btn btn-sm" onClick={() => setLines((ls) => [...ls, blank()])} data-testid="ap-add"><Icon name="plus" />{t("addLine")}</button>
            <button className="btn btn-sm btn-danger-ghost" disabled={!anySel} onClick={() => setLines((ls) => { const k = ls.filter((l) => !l.sel); return k.length ? k : [blank()]; })}><Icon name="trash" />{t("deleteSelected")}</button>
          </div>
          <div className="table-wrap"><table className="tbl lines-table je-lines" data-testid="ap-lines">
            <thead><tr><th className="w-chk" /><th className="w-idx">#</th><th>{t("statement")}</th><th>{t("fixedAssetKind")}</th><th className="end">{t("qty")}</th><th className="end">{t("price")}</th><th>{t("tax")}</th><th className="end">{t("total")}</th><th className="end">{t("delete")}</th></tr></thead>
            <tbody>{lines.map((l, i) => {
              const r = calc(l);
              return (
                <tr key={l.key} className={bad.has(l.key) ? "row-err" : ""}>
                  <td className="w-chk"><input type="checkbox" checked={!!l.sel} onChange={(e) => setLines((ls) => ls.map((x) => (x.key === l.key ? { ...x, sel: e.target.checked } : x)))} aria-label={t("selectRow")} /></td>
                  <td className="num muted">{i + 1}</td>
                  <td className="w-note"><input className="control" value={l.statement} maxLength={200} onChange={(e) => set(l.key, { statement: e.target.value })} aria-label={t("statement")} data-testid={`ap-st-${i}`} /></td>
                  <td className="w-sel"><select className="control" value={l.kindId} onChange={(e) => set(l.key, { kindId: e.target.value })} aria-label={t("fixedAssetKind")} data-testid={`ap-kind-${i}`}>
                    <option value="">{t("choose")}</option>{(o?.kinds ?? []).map((k) => <option key={k.id} value={k.id}>{L(k)}</option>)}</select></td>
                  <td className="w-amt"><input className="control num" dir="ltr" inputMode="numeric" value={l.qty} onChange={(e) => set(l.key, { qty: e.target.value })} aria-label={t("qty")} data-testid={`ap-qty-${i}`} /></td>
                  <td className="w-amt"><input className="control num" dir="ltr" inputMode="decimal" value={l.price} onChange={(e) => set(l.key, { price: e.target.value })} aria-label={t("price")} data-testid={`ap-price-${i}`} /></td>
                  <td className="w-sel"><select className="control" value={l.taxTypeId} onChange={(e) => set(l.key, { taxTypeId: e.target.value })} aria-label={t("tax")} data-testid={`ap-tax-${i}`}>
                    <option value="">{t("choose")}</option>{(o?.taxTypes ?? []).map((x) => <option key={x.id} value={x.id}>{L(x)}</option>)}</select></td>
                  <td className="end num"><b data-testid={`ap-total-${i}`}>{r ? amt(r.total) : "–"}</b></td>
                  <td className="end"><button className="btn btn-sm btn-icon btn-danger-ghost" onClick={() => setLines((ls) => { const k = ls.filter((x) => x.key !== l.key); return k.length ? k : [blank()]; })} aria-label={t("delete")}><Icon name="trash" /></button></td>
                </tr>
              );
            })}</tbody>
          </table></div>
          <div className={`dlg-alert ${alert ? "show" : ""}`} role="alert" data-testid="ap-alert">{alert && <><Icon name="alert" /><span>{alert}</span></>}</div>
        </Card>
      </div>
    </Shell>
  );
}

// ---------- purchase page ----------

function ReverseDialog({ x, onClose, onDone }: { x: AssetInvoiceDto; onClose(): void; onDone(r: AssetInvoiceDto): void }) {
  const { t } = useLang();
  const toast = useToast();
  const [at, setAt] = useState(() => (nowLocal() < x.invoiceAt ? x.invoiceAt : nowLocal()));
  const [statement, setStatement] = useState(() => t("voucherReversalOf", { x: x.code }));
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [alert, setAlert] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function submit() {
    setBusy(true); setAlert(null);
    try {
      const body: InvoiceReverseInput = { reversedAt: at, statement };
      const r = await api<AssetInvoiceDto>("POST", `/asset-invoices/${x.id}/reverse`, body);
      toast(t("assetReversedOk", { x: x.code, y: r.reversalEntry?.number ?? "" }));
      onDone(r);
    } catch (err) { const f = fieldErrorsOf(t, err); setErrs(f); setAlert(f.reversedAt ?? f.statement ?? errorText(t, err)); } finally { setBusy(false); }
  }
  return (
    <Modal title={`${t("journalReverse")} ${x.code}`} sub={t("assetReverseSub")} onClose={onClose} onSubmit={() => void submit()} alert={alert} busy={busy} saveLabel={t("journalReverse")}>
      <Field label={t("dateTime")} req htmlFor="rv-dt" error={errs.reversedAt}><input id="rv-dt" className="control num" style={{ textAlign: "start" }} type="datetime-local" value={at} min={x.invoiceAt} onChange={(e) => setAt(e.target.value)} /></Field>
      <Field label={t("statement")} htmlFor="rv-st" full error={errs.statement}><input id="rv-st" className="control" value={statement} maxLength={500} onChange={(e) => setStatement(e.target.value)} /></Field>
    </Modal>
  );
}

export function AssetPurchaseShow() {
  const { id } = useParams();
  const { t, L } = useLang();
  const s = useSession();
  const [x, setX] = useState<AssetInvoiceDto | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [reversing, setReversing] = useState(false);
  useEffect(() => {
    setX(null); setFailed(null);
    api<AssetInvoiceDto>("GET", `/asset-invoices/${id}`).then(setX, (err) => setFailed(err instanceof ApiFailure && err.status === 404 ? t("assetNotFound") : errorText(t, err)));
  }, [id, t]);
  if (!x) return (
    <Shell navKey="/assets/purchases"><PageHead crumbs={assetCrumbs(t, t("assetPurchases"), "/assets/purchases")} title={t("assetInvoice")} />
      <section className="card"><div className={`state-box ${failed ? "error" : ""}`}>{failed ? <><Icon name="alert" /><h3>{t("loadErrorTitle")}</h3><p>{failed}</p></> : <p>{t("loading")}</p>}</div></section>
    </Shell>
  );
  const je = (r: { id: string; number: string }) => <Link className="num" to={`/journals/show/${r.id}`}>{r.number}</Link>;
  const canReverse = s.can(INV.add) && s.can(INV.del);
  const actions = <>
    {!x.reversedAt && canReverse && (x.reversible
      ? <button className="btn" onClick={() => setReversing(true)} data-testid="ap-reverse"><Icon name="undo" />{t("journalReverse")}</button>
      : <button className="btn" disabled title={t("cantDeleteAssetInvoice")} data-testid="ap-reverse"><Icon name="lock" />{t("journalReverse")}</button>)}
    <button className="btn btn-primary" onClick={() => window.print()}><Icon name="printer" />{t("print")}</button>
  </>;
  const kv: [string, ReactNode, string?][] = [
    [t("invoiceNo"), <b className="num">{x.code}</b>], [t("status"), <Status x={x} />], [t("invoiceDateTime"), <span className="num">{when(x.invoiceAt)}</span>],
    [t("supplier"), <Link to={`/Supplier/${x.party.id}`}>{x.party.name}</Link>], [t("warehouse"), L(x.warehouse)], [t("treasury"), x.treasury ? L(x.treasury) : "–"],
    [t("paidReference"), x.reference ? <span className="num">{x.reference}</span> : "–"], [t("by"), x.createdBy || "–"]
  ];
  if (x.journalEntry) kv.push([t("voucherJournal"), je(x.journalEntry)]);
  if (x.reversalEntry) kv.push([t("voucherReversalEntry"), <>{je(x.reversalEntry)}{x.reversalAt && <> · <span className="num">{when(x.reversalAt)}</span></>}</>]);
  kv.push([t("note"), x.note || <span className="muted">–</span>, "span-2"]);
  const due = /[1-9]/.test(x.remaining);
  return (
    <Shell navKey="/assets/purchases">
      <PageHead crumbs={assetCrumbs(t, t("assetPurchases"), "/assets/purchases", [[x.code]])} title={`${t("assetInvoice")} ${x.code}`} actions={actions} />
      <AssetProvisional />
      {!x.reversedAt && !x.reversible && <div className="dep-note" data-testid="ap-locked"><Icon name="lock" /><span>{t("cantDeleteAssetInvoice")}</span></div>}
      <div className="detail-grid"><div className="detail-main">
        <Card title={t("invoiceData")}><dl className="kv" style={{ ["--kv-cols" as string]: 3 }} data-testid="ap-data">{kv.map(([k, v, cls]) => <div key={k} className={cls}><dt>{k}</dt><dd>{v}</dd></div>)}</dl></Card>
        <Card title={t("invoiceItems")} count={x.lines.length} raw>
          <div className="table-wrap"><table className="tbl detail-lines" data-testid="ap-show-lines">
            <thead><tr><th>#</th><th>{t("statement")}</th><th>{t("fixedAssetKind")}</th><th className="end">{t("qty")}</th><th className="end">{t("price")}</th><th>{t("tax")}</th><th className="end">{t("taxAmount")}</th><th className="end">{t("total")}</th></tr></thead>
            <tbody>{x.lines.map((l) => <tr key={l.lineNo}><td className="num muted">{l.lineNo}</td><td><span className="cell-name">{l.statement}</span></td><td>{L(l.kind)}</td>
              <td className="end num">{l.qty.replace(/\.?0+$/, "")}</td><td className="end num">{amt(l.price)}</td><td>{L(l.taxType)}</td><td className="end num">{amt(l.tax)}</td><td className="end num"><b>{amt(l.total)}</b></td></tr>)}</tbody>
          </table></div>
          <div className="totals-band q" data-testid="ap-show-totals">
            <div><span>{t("subTotal")}</span><b className="num">{amt(x.subtotal)}</b></div><div><span>{t("tax")}</span><b className="num">{amt(x.vat)}</b></div>
            <div className="g"><span>{t("grandTotal")}</span><b className="num">{amt(x.grand)} <small>{t("currency")}</small></b></div>
            <div className="paidc"><span>{t("paid")}</span><b className="num">{amt(x.paid)}</b></div>
            <div className={due ? "due" : "ok"}><span>{t("remaining")}</span><b className="num" data-testid="ap-show-remaining">{amt(x.remaining)}</b></div>
          </div>
        </Card>
      </div><aside className="detail-side">
        <Card title={t("linkedAssets")} count={x.assets.length}>
          {x.assets.length ? <ul className="rel-list" data-testid="ap-assets">{x.assets.map((a) => (
            <li key={a.id}><Link to={`/assets/depreciation?asset=${a.id}`}>{a.title}</Link>{a.cancelled && <span className="pill">{t("assetCancelled")}</span>}
              <span className="muted num">{amt(a.monthly)}/{t("monthShort")}</span><b className="num">{amt(a.amount)}</b></li>
          ))}</ul> : <div className="empty sm"><Icon name="building" /><div>–</div></div>}
        </Card>
      </aside></div>
      {reversing && <ReverseDialog x={x} onClose={() => setReversing(false)} onDone={(r) => { setReversing(false); setX(r); }} />}
    </Shell>
  );
}
